import { AppShellSkeleton, AuthPageSkeleton } from '@/components/core';
import { isPublicRoute } from '@/constant/auth-public-routes';
import { SidebarProvider } from '@/components/ui-kit/sidebar';
import { Toaster } from '@/components/ui-kit/toaster';
import {
  HRM_MANAGE_PERMISSIONS,
  SKILL_MANAGER_PERMISSIONS,
} from '@/constant/hr/manage-permissions';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useLanguageContext } from '@/i18n/language-context';
import { MainLayout } from '@/layout/main-layout/main-layout';
import {
  ManageAttendancePage,
  MyAttendancePage,
  UserAttendanceHistoryPage,
} from '@/modules/attendance';
import { NotFoundPage, ServiceUnavailablePage } from '@/modules/error-view';
import { EmployeeDetailsPage, EmployeeFormPage, EmployeeListPage } from '@/modules/hr-directory';
import { LogsPage } from '@/modules/hr-logs';
import { LeaveCalendarPage, LeaveManagerPage, MyLeavePage, SickLeavePage } from '@/modules/leave';
import { ManageLunchPage, RequestLunchPage } from '@/modules/lunch';
import {
  CtcLevelManagerPage,
  DepartmentManagerPage,
  DesignationManagerPage,
  HolidayPage,
  KpiConfigurationPage,
  LeaveBuilderPage,
  LocationManagerPage,
  NocTypeManagerPage,
  ProjectManagerPage,
  RoleManagerPage,
  SKILL_MANAGER_TABS,
  SkillManagerPage,
} from '@/modules/org-setup';
import { OvertimeDetailsPage, OvertimeManagerPage } from '@/modules/overtime';
import {
  FormEditorPage,
  FormListPage,
  PeerRequestsPage,
  PerformanceDashboardPage,
  ReviewDetailsPage,
  ReviewsListPage,
  SubmissionAnswerPage,
  SubmissionsListPage,
} from '@/modules/performance';
import { ProfileCompletionGate, ProfileEditPage, ProfilePage } from '@/modules/profile';
import {
  OfferBuilderPage,
  OfferListPage,
  ProfileBuilderPage,
  ProfileListPage,
} from '@/modules/recruitment';
import {
  BusinessCardPage,
  ByodApplyPage,
  ByodPage,
  IdCardApplyPage,
  IdCardPage,
  MetlifeApplyPage,
  MetlifePage,
  NocApplyPage,
  NocPage,
  NocReportPage,
} from '@/modules/service-desk';
import {
  BatchDetailsPage,
  ResponseViewPage,
  SurveyFormEditorPage,
  SurveyFormListPage,
  SurveyListPage,
  SubmissionAnswerPage as SurveySubmissionAnswerPage,
  SubmissionsListPage as SurveySubmissionsListPage,
} from '@/modules/survey';
import {
  MbmProjectManagerPage,
  MyTimesheetPage,
  TimesheetManagerPage,
  UserTimesheetDetailPage,
} from '@/modules/timesheet';
import { MyToilPage, ToilManagerPage } from '@/modules/toil';
import { ClientMiddleware } from '@/state/client-middleware';
import { OrganizationTimeZoneGate } from '@/state/organization-time-zone-gate';
import { Guard } from '@/state/store/auth/guard';
import { ProtectedRoute } from '@/state/store/auth/protected-route';
import { ThemeProvider } from '@/styles/theme/theme-provider';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthRoutes } from './auth.route';

/** `/hr/directory/new/create` adds a person (create permission); any other id edits one (update permission). */
const EmployeeFormRoute = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const permission =
    employeeId === 'new' ? HRM_PERMISSIONS.DIRECTORY.CREATE : HRM_PERMISSIONS.DIRECTORY.UPDATE;

  return (
    <ProtectedRoute key={permission} permissions={[permission]}>
      <EmployeeFormPage />
    </ProtectedRoute>
  );
};

export const AppRoutes = () => {
  const { isInitialLoading } = useLanguageContext();

  // The first translations are loading: show the frame of the page being opened (sign-in or app).
  if (isInitialLoading) {
    const isAuthPage = isPublicRoute(window.location.pathname);
    return isAuthPage ? <AuthPageSkeleton /> : <AppShellSkeleton />;
  }
  return (
    <div className="min-h-dvh bg-background font-sans antialiased relative">
      <ClientMiddleware>
        <ThemeProvider>
          <SidebarProvider>
            <Routes>
              {AuthRoutes}
              <Route
                element={
                  <Guard>
                    <OrganizationTimeZoneGate>
                      <ProfileCompletionGate>
                        <MainLayout />
                      </ProfileCompletionGate>
                    </OrganizationTimeZoneGate>
                  </Guard>
                }
              >
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/edit" element={<ProfileEditPage />} />
                <Route
                  path="/logs"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LOGS.VIEW]}>
                      <LogsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/department-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.DEPARTMENT}>
                      <DepartmentManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/role-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.ROLE}>
                      <RoleManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/designation-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.DESIGNATION}>
                      <DesignationManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/location-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.LOCATION}>
                      <LocationManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/project-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.PROJECT}>
                      <ProjectManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/skill-manager"
                  element={
                    <ProtectedRoute permissions={SKILL_MANAGER_PERMISSIONS}>
                      <SkillManagerPage />
                    </ProtectedRoute>
                  }
                />
                {/* The skill categories and sub-categories pages are tabs of the Skill Manager now. */}
                <Route
                  path="/hr/skill-category-manager"
                  element={
                    <Navigate
                      to={`/hr/skill-manager?tab=${SKILL_MANAGER_TABS.CATEGORIES}`}
                      replace
                    />
                  }
                />
                <Route
                  path="/hr/skill-sub-category-manager"
                  element={
                    <Navigate
                      to={`/hr/skill-manager?tab=${SKILL_MANAGER_TABS.SUB_CATEGORIES}`}
                      replace
                    />
                  }
                />
                <Route
                  path="/hr/noc-type-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.NOC_TYPE}>
                      <NocTypeManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/ctc-level-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.CTC_LEVEL}>
                      <CtcLevelManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/kpi-configuration"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.KPI.CONFIGURE]}>
                      <KpiConfigurationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/directory"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.DIRECTORY.VIEW]}>
                      <EmployeeListPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/hr/directory/:employeeId/create" element={<EmployeeFormRoute />} />
                <Route
                  path="/hr/directory/:employeeId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.DIRECTORY.VIEW]}>
                      <EmployeeDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-attendance"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.ATTENDANCE.VIEW_OWN]}>
                      <MyAttendancePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/manage-attendance"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.ATTENDANCE.VIEW_ALL]}>
                      <ManageAttendancePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/manage-attendance/:userId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.ATTENDANCE.VIEW_ALL]}>
                      <UserAttendanceHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/leave-builder"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.LEAVE_CATEGORY}>
                      <LeaveBuilderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/holiday"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.HOLIDAY}>
                      <HolidayPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-leave"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LEAVE.VIEW_OWN]}>
                      <MyLeavePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/leave-calendar"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LEAVE.VIEW_CALENDAR]}>
                      <LeaveCalendarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/leave-manager"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LEAVE.VIEW_ALL]}>
                      <LeaveManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/sick-leave"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SICK_LEAVE.VIEW]}>
                      <SickLeavePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-toil"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.TOIL.VIEW_OWN]}>
                      <MyToilPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/toil-manager"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.TOIL.VIEW_ALL]}>
                      <ToilManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/overtime-manager"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.OVERTIME.VIEW]}>
                      <OvertimeManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/overtime-manager/:userId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.OVERTIME.VIEW]}>
                      <OvertimeDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-timesheet"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.TIMESHEET.VIEW_OWN]}>
                      <MyTimesheetPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/timesheet-manager"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.TIMESHEET.VIEW_ALL]}>
                      <TimesheetManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/timesheet-manager/:userId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.TIMESHEET.VIEW_ALL]}>
                      <UserTimesheetDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/mbm-project-manager"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.MBM_PROJECT}>
                      <MbmProjectManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/form-creation"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.PERFORMANCE_FORM}>
                      <FormListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/form-creation/:formId"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.PERFORMANCE_FORM}>
                      <FormEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/reviews"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PERFORMANCE_REVIEW.VIEW]}>
                      <ReviewsListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/reviews/:reviewId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PERFORMANCE_REVIEW.VIEW]}>
                      <ReviewDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-submissions"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PERFORMANCE_SUBMISSION.VIEW_OWN]}>
                      <SubmissionsListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/my-submissions/:pmReviewDetailId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PERFORMANCE_SUBMISSION.VIEW_OWN]}>
                      <SubmissionAnswerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/peer-requests"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PEER_REQUEST.VIEW]}>
                      <PeerRequestsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/request-lunch"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LUNCH.VIEW_OWN]}>
                      <RequestLunchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/manage-lunch"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.LUNCH.VIEW_ALL]}>
                      <ManageLunchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/potential-profiles"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.POTENTIAL_PROFILE.VIEW]}>
                      <ProfileListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/potential-profiles/builder"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.POTENTIAL_PROFILE.CREATE]}>
                      <ProfileBuilderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/conditional-offers"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.CONDITIONAL_OFFER.VIEW]}>
                      <OfferListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/conditional-offers/builder"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.CONDITIONAL_OFFER.CREATE]}>
                      <OfferBuilderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/survey-form-creation"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.SURVEY_FORM}>
                      <SurveyFormListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/survey-form-creation/:formId"
                  element={
                    <ProtectedRoute permissions={HRM_MANAGE_PERMISSIONS.SURVEY_FORM}>
                      <SurveyFormEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/surveys"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SURVEY.VIEW]}>
                      <SurveyListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/surveys/:batchId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SURVEY.VIEW]}>
                      <BatchDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/surveys/:batchId/:submissionId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SURVEY.VIEW_RESPONSE]}>
                      <ResponseViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/survey-my-submissions"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SURVEY_SUBMISSION.VIEW_OWN]}>
                      <SurveySubmissionsListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/survey-my-submissions/:submissionId"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.SURVEY_SUBMISSION.VIEW_OWN]}>
                      <SurveySubmissionAnswerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk"
                  element={<Navigate to="/hr/service-desk/noc" replace />}
                />
                <Route
                  path="/hr/service-desk/noc"
                  element={
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.NOC_APPLICATION.VIEW,
                        HRM_PERMISSIONS.NOC_APPLICATION.VIEW_REQUESTS,
                      ]}
                    >
                      <NocPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/noc/apply"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.NOC_APPLICATION.APPLY]}>
                      <NocApplyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/noc/report/:id"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.NOC_APPLICATION.GENERATE]}>
                      <NocReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/byod"
                  element={
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.BYOD_APPLICATION.VIEW,
                        HRM_PERMISSIONS.BYOD_APPLICATION.VIEW_REQUESTS,
                      ]}
                    >
                      <ByodPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/byod/apply"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.BYOD_APPLICATION.APPLY]}>
                      <ByodApplyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/business-card"
                  element={
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.BUSINESS_CARD_APPLICATION.VIEW,
                        HRM_PERMISSIONS.BUSINESS_CARD_APPLICATION.VIEW_REQUESTS,
                      ]}
                    >
                      <BusinessCardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/id-card"
                  element={
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.ID_CARD_APPLICATION.VIEW,
                        HRM_PERMISSIONS.ID_CARD_APPLICATION.VIEW_REQUESTS,
                      ]}
                    >
                      <IdCardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/id-card/apply"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.ID_CARD_APPLICATION.APPLY]}>
                      <IdCardApplyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/insurance"
                  element={
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.METLIFE_APPLICATION.VIEW,
                        HRM_PERMISSIONS.METLIFE_APPLICATION.VIEW_REQUESTS,
                      ]}
                    >
                      <MetlifePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/service-desk/insurance/apply"
                  element={
                    // Reviewers (update-status) open the form to edit someone's application.
                    <ProtectedRoute
                      permissions={[
                        HRM_PERMISSIONS.METLIFE_APPLICATION.APPLY,
                        HRM_PERMISSIONS.METLIFE_APPLICATION.UPDATE_STATUS,
                      ]}
                    >
                      <MetlifeApplyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hr/performance-dashboard"
                  element={
                    <ProtectedRoute permissions={[HRM_PERMISSIONS.PERFORMANCE_DASHBOARD.VIEW]}>
                      <PerformanceDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/503" element={<ServiceUnavailablePage />} />
                <Route path="/404" element={<NotFoundPage />} />
              </Route>

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/hr/directory" replace />} />
              <Route path="/dashboard" element={<Navigate to="/hr/directory" replace />} />

              <Route path="*" element={<Navigate to="/404" />} />
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
};
